import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-custom-dark-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-custom-dark-background dark:border-input dark:hover:bg-custom-dark-background-hover',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        muted: 'bg-muted text-foreground hover:bg-muted/90',
        grey: 'bg-matte-800 text-matte-200 border hover:bg-matte-800/80',
        'grey-text-light':
          'text-matte-100 bg-[#212121] border border-[#383838] hover:bg-[#212121]/80',
        'grey-light':
          'bg-[#212121] border border-[#383838] hover:bg-[#212121]/80 text-[#909090]',
        'grey-primary-xlight': 'text-primary-xlight bg-matte-900 border',
        // Name needs to be changed later
        'outline-accent-xlight':
          'text-primary-xlight bg-[#1f1f1f] border border-[#353535] shadow-matte-dark hover:bg-[#1f1f1f]/80',
        matte:
          'text-matte-400 bg-matte-800 border shadow-matte-dark hover:bg-matte-800/70',
        'matte-dark':
          'text-matte-400 bg-matte-900 border shadow-matte-dark hover:bg-matte-900/70',
        'matte-dark-xlight':
          'text-primary-xlight bg-matte-900 border shadow-matte-dark hover:bg-matte-900/70',
        'matte-primary':
          'text-primary-foreground bg-primary border border-primary-xlight shadow-matte-dark hover:bg-primary/90',
        'matte-primary-xlight':
          'text-primary-xlight bg-matte-800 border shadow-matte-dark hover:bg-matte-800/70',
        'secondary-light': 'bg-matte-100 text-primary hover:bg-matte-100/90',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
